/**
 * Local-storage draft persistence — safety net against autosave loss when
 * a session expires, network blips, or the tab is killed mid-edit.
 *
 * Each call site picks a `key` that uniquely identifies the surface +
 * entity being edited. Different surfaces should namespace their keys to
 * avoid collisions, e.g. `patient-notes:${patientUuid}`,
 * `clinical-history:${patientUuid}:${category}`, `consent-form:${formId}`.
 *
 * The notes editor uses an un-namespaced key (just the note id) for
 * backward-compat with in-flight drafts that pre-date this extraction.
 *
 * Storage is fire-and-forget — failures (quota exceeded, private-mode
 * restrictions) never throw. Drafts are best-effort recovery, not a
 * primary data path.
 */

const PREFIX = 'claro:draft:'

/** Saves a draft payload. Failures are swallowed (storage is best-effort). */
export function saveDraft<T extends object>(key: string, payload: T): void {
  try {
    localStorage.setItem(
      `${PREFIX}${key}`,
      JSON.stringify({ ...payload, ts: Date.now() }),
    )
  } catch {
    // storage full / unavailable — non-critical
  }
}

/** Loads a draft payload, or null if none / unreadable. */
export function loadDraft<T extends object>(
  key: string,
): (T & { ts: number }) | null {
  try {
    const raw = localStorage.getItem(`${PREFIX}${key}`)
    return raw ? (JSON.parse(raw) as T & { ts: number }) : null
  } catch {
    return null
  }
}

/** Clears a draft. Failures are swallowed. */
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(`${PREFIX}${key}`)
  } catch {
    // ignore
  }
}
